import {
    Tabs, 
    Tab,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import { ArrowBack, ContentCopy, OpenInNew, Stop, Replay, PlayArrow, InfoOutlined, SkipNext, AccessTime } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {ColDef, ValueFormatterParams} from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { useSnackbar } from "notistack";
import {urls} from "@routes";
import { format } from "date-fns";
import GoogleLogo from "@assets/images/google_g_logo.svg"
import MicrosoftLogo from "@assets/images/microsoft.svg"
import YahooLogo from "@assets/images/yahoo.svg"
import ZohoLogo from "@assets/images/zoho.svg"
import { WorkspaceDetail, SelectWorkspacePopup } from "@admin/AdminUserDetails";
import OutlookLogo from "@assets/images/outlook.png"

export const ESP_LOGOS: Record<string, { src: string; label: string }> = {
    google:    { src: GoogleLogo,    label: "Google" },
    microsoft: { src: MicrosoftLogo, label: "Microsoft" },
    yahoo:     { src: YahooLogo,     label: "Yahoo" },
    zoho:      { src: ZohoLogo,      label: "Zoho" },
    microsoft_consumer:   { src: OutlookLogo, label: "Outlook"},
}

interface Contact {
    uid: string;
    email: string;
    bounced: boolean;
    unsubscribed: boolean;
    esp: string;
    verification_status?: string;
    ver_deliverable?: boolean | null;
    ver_full_inbox?: boolean | null;
    ver_catch_all?: boolean | null;
    ver_host_exists?: boolean | null;
    ver_disposable?: boolean | null;
    ver_dangerous?: boolean | null;
    ver_esp?: string | null;
    ver_message?: string | null;
    [key: string]: any;
}

type EspBreakdownKey = "google" | "microsoft" | "yahoo" | "zoho" | "others" | "unknown";

type EspBreakdownSummaryKey =
    | "contacts_after_verification"
    | "invalid_emails"
    | "unverifiable_emails"
    | "disposable_emails"
    | "dangerous_emails"
    | "catch_all_emails"
    | "webmail_emails"
    | "full_inbox_emails";

type EspBreakdown = Record<EspBreakdownKey, number>;

interface SummaryCards {
    catch_all_emails: number;
    contacts_after_verification: number;
    contacts_uploaded: number;
    dangerous_emails: number;
    disposable_emails: number;
    full_inbox_emails: number;
    invalid_emails: number;
    unverifiable_emails: number;
    webmail_emails: number;
    skipped_emails: number;
    unsubscribed: number;
    unverified_emails: number;
    esp_breakdowns?: Partial<Record<EspBreakdownSummaryKey, EspBreakdown>>;
}

type EmailVerificationStatus = "N/A" | "queued" | "processing" | "completed" | "skipping" | "skipped" | "failed";

interface PageData {
    list_name: string;
    contacts: Contact[];
    attribute_column_headers: string[];
    summary_cards?: SummaryCards | Record<string, never>;
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
    email_verification_status: EmailVerificationStatus;
    verification_task_uid: string | null;
    list_status: string;
    processed_contacts_count: number;
    total_contacts_count: number;
    verification_summary?: Record<string, number>;
}

interface PaginationData {
    data: Contact[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
}

// Maps a verification_status to the friendly label + MUI Chip color shown in the status column.
// Mirrors getStatusDisplay() on the user-side ContactListDetails page.
function getStatusDisplay(status: string | null | undefined): {
    label: string;
    color: "default" | "error" | "warning" | "success";
} {
    if (!status || status === "pending") return { label: "Verifying", color: "default" };
    switch (status.toLowerCase()) {
        case "valid":
            return { label: "Safe To Send", color: "success" };
        case "invalid_format":
            return { label: "Not Email Format", color: "error" };
        case "dangerous":
            return { label: "Unsafe to Send", color: "error" };
        case "disposable":
            return { label: "Temporary Email", color: "warning" };
        case "unverifiable":
            return { label: "Unable to Verify", color: "warning" };
        case "catchall":
            return { label: "Catch All Email", color: "warning" };
        case "email_not_exists":
            return { label: "Email Doesn't Exists", color: "error" };
        case "full_inbox":
            return { label: "Email Inbox Full", color: "warning" };
        case "skipped":
            return { label: "Skipped Verification", color: "default" };
        default:
            return { label: status, color: "default" };
    }
}

// Per-status Yes / No / dash matrix for each derived verification column.
type CellValue = "yes" | "no" | "dash";
const columnMatrix: Record<string, Partial<Record<string, CellValue>>> = {
    invalid_format: { ver_invalid: "yes" },
    disposable: { ver_invalid: "no", ver_disposable: "yes", ver_catchall: "yes" },
    dangerous: { ver_dangerous: "yes" },
    // Unverifiable = we could not verify the mailbox (greylist / timeout). It is NOT invalid,
    // so every derived column falls back to a dash rather than implying a result.
    unverifiable: {},
    catchall: { ver_invalid: "no", ver_catchall: "yes" },
    email_not_exists: { ver_invalid: "yes" },
    valid: { ver_invalid: "no", ver_disposable: "no", ver_dangerous: "no", ver_full_inbox: "no", ver_catchall: "no" },
    full_inbox: { ver_invalid: "no", ver_disposable: "no", ver_dangerous: "no", ver_full_inbox: "yes", ver_catchall: "no" },
    // Skipped contacts were never probed — every derived column falls back to a dash.
    skipped: {},
};

const verificationColumns: { id: string; label: string }[] = [
    { id: "ver_invalid", label: "Invalid Email" },
    { id: "ver_disposable", label: "Disposable Email" },
    { id: "ver_dangerous", label: "Dangerous Email" },
    { id: "ver_full_inbox", label: "Inbox Full" },
    { id: "ver_catchall", label: "Catch All" },
];

// Column labels for the "Customize Columns" toggle menu.
const columnLabels: Record<string, string> = {
    email: "Contact Email ID",
    uid: "Contact UID",
    verification_status: "Verification Status",
    ver_invalid: "Invalid Email",
    ver_disposable: "Disposable Email",
    ver_dangerous: "Dangerous Email",
    ver_full_inbox: "Inbox Full",
    ver_catchall: "Catch All",
    bounced: "Bounced",
    unsubscribed: "Unsubscribed",
    ver_deliverable: "Deliverable",
    ver_host_exists: "Host Exists",
    ver_esp: "ESP",
    ver_message: "Message",
};

export default function AdminUserContactListDetails() {
    const { userId, contactListUID } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    const [sortField, setSortField] = useState("");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [listName, setListName] = useState("");
    const [pageData, setPageData] = useState<PaginationData>();
    const [additionalColumnNames, setAdditionalColumnNames] = useState<string[]>([]);
    const [summaryCards, setSummaryCards] = useState<SummaryCards | null>(null);
    const [verificationStatus, setVerificationStatus] = useState<EmailVerificationStatus>("N/A");
    const [verificationTaskUid, setVerificationTaskUid] = useState<string | null>(null);
    const [processedCount, setVerifiedCount] = useState(0);
    const [totalContactsCount, setTotalContactsCount] = useState(0);
    const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
    const [tabValue, setTabValue] = useState<number>(0);

    // Identifying prefix used for invalidations. The page query key below extends this prefix so
    // TanStack's prefix-matching correctly refetches it on mutate / websocket / progress events.
    const queryKey = ["adminContactListDetails", userId, contactListUID];
    const ws = useRef<WebSocket | null>(null);

    const pageDataQuery = useQuery({
        queryKey: [
            "adminContactListDetails",
            userId,
            contactListUID,
            page,
            pageSize,
            search,
            sortField,
            sortDirection,
        ],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/admin-contact-list-details/?user_id=${userId}&uid=${contactListUID}&page=${page}&page_size=${pageSize}&search=${encodeURIComponent(search)}&sort_field=${encodeURIComponent(sortField)}&sort_direction=${sortDirection}`
            ),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            const data = pageDataQuery.data.data as PageData;
            setListName(data.list_name);
            setPageData({
                data: data.contacts || [],
                total_count: data.total_count,
                total_pages: data.total_pages,
                current_page: data.current_page,
                page_size: data.page_size,
            });
            setAdditionalColumnNames(
                data.attribute_column_headers.filter((h) => h !== "Email Address")
            );
            setSummaryCards(
                data.summary_cards && Object.keys(data.summary_cards).length > 0
                    ? (data.summary_cards as SummaryCards)
                    : null
            );
            setVerificationStatus(data.email_verification_status ?? "N/A");
            setVerificationTaskUid(data.verification_task_uid ?? null);
            setVerifiedCount(data.processed_contacts_count ?? 0);
            setTotalContactsCount(data.total_contacts_count ?? 0);
        }
    }, [pageDataQuery.data]);

    // Lightweight polling for live progress while verification is processing.
    const verificationProgressQuery = useQuery({
        queryKey: ["adminContactListVerificationProgress", userId, contactListUID],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/admin-contact-list-verification-progress/?user_id=${userId}&uid=${contactListUID}`
            ),
        gcTime: 0,
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        enabled: verificationStatus === "queued" || verificationStatus === "processing" || verificationStatus === "skipping",
        refetchInterval: 5000,
    });

    useEffect(() => {
        if (verificationProgressQuery.data) {
            const data = verificationProgressQuery.data.data;
            setVerifiedCount(data.processed_contacts_count ?? 0);
            setTotalContactsCount(data.total_contacts_count ?? 0);
            const polledStatus = data.email_verification_status;
            if (polledStatus) {
                // Keep the badge in sync as the job moves queued -> processing -> done.
                setVerificationStatus(polledStatus as EmailVerificationStatus);
                // "queued"/"processing"/"skipping" are non-terminal — keep polling and only
                // refetch the full table once verification reaches a terminal state.
                if (polledStatus !== "queued" && polledStatus !== "processing" && polledStatus !== "skipping") {
                    queryClient.invalidateQueries({ queryKey });
                }
            }
        }
    }, [verificationProgressQuery.data]);

    useEffect(() => {
        if (!userId) return;

        const wsUrl = `${process.env.REACT_APP_WS_HOST}/ws/contact-list/${userId}/`;
        ws.current = new WebSocket(wsUrl);

        ws.current.onmessage = (event) => {
            // Backend wraps the payload one extra level deep — unwrap the outer envelope first.
            const msg = JSON.parse(event.data)["event_data"];
            const eventName: string = msg["event_name"];
            const eventData: any = msg["event_data"];

            if (
                eventName === "contact_list_status_update" &&
                eventData.contact_list_uid === contactListUID
            ) {
                if (eventData.email_verification_status) {
                    setVerificationStatus(eventData.email_verification_status as EmailVerificationStatus);
                }
                queryClient.invalidateQueries({ queryKey });
            }
        };

        return () => {
            ws.current?.close();
        };
    }, [userId, contactListUID]);

    const triggerVerificationMutation = useMutation({
        mutationFn: () =>
            authenticateAndPostData("/admin/admin-trigger-contact-list-verification/", {
                user_id: userId,
                uid: contactListUID,
            }),
        onSuccess: () => {
            enqueueSnackbar("Verification started.", { variant: "success" });
            setVerificationStatus("processing");
            queryClient.invalidateQueries({ queryKey });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.data?.message || "Failed to start verification.", {
                variant: "error",
            });
        },
    });

    const stopVerificationMutation = useMutation({
        mutationFn: () =>
            authenticateAndPostData("/admin/admin-stop-contact-list-verification/", {
                user_id: userId,
                uid: contactListUID,
            }),
        onSuccess: () => {
            enqueueSnackbar("Verification stopped.", { variant: "success" });
            queryClient.invalidateQueries({ queryKey });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.data?.message || "Failed to stop verification.", {
                variant: "error",
            });
        },
    });

    const skipVerificationMutation = useMutation({
        mutationFn: () =>
            authenticateAndPostData("/admin/admin-skip-contact-list-verification/", {
                user_id: userId,
                uid: contactListUID,
            }),
        onSuccess: () => {
            // Move into the transient "skipping" state immediately so the Skip/Stop buttons
            // disable right away. The backend finalises asynchronously; the progress poll /
            // websocket flips the status to "skipped" when done.
            enqueueSnackbar("Skipping verification…", { variant: "success" });
            setVerificationStatus("skipping");
            queryClient.invalidateQueries({ queryKey });
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.data?.message || "Failed to skip verification.", {
                variant: "error",
            });
        },
    });

    const handleCopyUid = () => {
        if (verificationTaskUid) {
            navigator.clipboard.writeText(verificationTaskUid);
            enqueueSnackbar("Task UID copied to clipboard.", { variant: "info" });
        }
    };

    const handleTabChange = async (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const verificationStatusChip = () => {
        switch (verificationStatus) {
            case "queued":
                return <Chip size="small" color="warning" icon={<AccessTime />} label="Queued" />;
            case "processing":
                return <Chip size="small" color="warning" label="Processing..." />;
            case "completed":
                return <Chip size="small" color="success" label="Completed" />;
            case "skipping":
                return <Chip size="small" color="warning" label="Skipping..." />;
            case "skipped":
                return <Chip size="small" color="default" label="Skipped" />;
            case "failed":
                return <Chip size="small" color="error" label="Failed" />;
            default:
                return <Chip size="small" color="default" label="N/A" />;
        }
    };

    const actionButton = () => {
        if (verificationStatus === "queued") {
            // Queued: the verifier hasn't started this job yet. Only Skip is offered (mirrors
            // the user-side page) — there's no in-progress run to Stop.
            return (
                <Tooltip title="Skip verification for the remaining contacts & keep the ones already verified. Skipped contacts can still be emailed based on campaign settings.">
                    <span>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<SkipNext />}
                            disabled={skipVerificationMutation.isPending}
                            onClick={() => skipVerificationMutation.mutate()}
                            sx={{ textTransform: "none" }}
                        >
                            Skip
                        </Button>
                    </span>
                </Tooltip>
            );
        }
        if (verificationStatus === "processing" || verificationStatus === "skipping") {
            // While "skipping", the finalize task is committing — disable both actions so the
            // skip can't be re-triggered (or overridden) mid-flight.
            const isSkipping = verificationStatus === "skipping";
            return (
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Skip verification for the remaining contacts & keep the ones already verified. Skipped contacts can still be emailed based on campaign settings.">
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<SkipNext />}
                                disabled={skipVerificationMutation.isPending || isSkipping}
                                onClick={() => skipVerificationMutation.mutate()}
                                sx={{ textTransform: "none" }}
                            >
                                {isSkipping ? "Skipping..." : "Skip"}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Stop />}
                        disabled={stopVerificationMutation.isPending || isSkipping}
                        onClick={() => setStopConfirmOpen(true)}
                        sx={{ textTransform: "none" }}
                    >
                        Stop
                    </Button>
                </Stack>
            );
        }
        if (verificationStatus === "completed") {
            return (
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Replay />}
                    disabled={triggerVerificationMutation.isPending}
                    onClick={() => triggerVerificationMutation.mutate()}
                    sx={{ textTransform: "none" }}
                >
                    Reverify
                </Button>
            );
        }
        return (
            <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PlayArrow />}
                disabled={triggerVerificationMutation.isPending}
                onClick={() => triggerVerificationMutation.mutate()}
                sx={{ textTransform: "none" }}
            >
                Start Verification
            </Button>
        );
    };

    const progressPercent =
        totalContactsCount > 0 ? Math.round((processedCount / totalContactsCount) * 100) : 0;

    // -------------- Cell renderers --------------
    const dashCell = <Typography color="text.secondary">—</Typography>;

    const yesNoBadge = (isYes: boolean) =>
        isYes ? (
            <Chip size="small" color="error" label="Yes" />
        ) : (
            <Chip size="small" color="success" label="No" />
        );

    const bouncedCell = (props: CustomCellRendererProps) => {
        if (verificationStatus === "queued" || verificationStatus === "processing" || verificationStatus === "skipping") return dashCell;
        return yesNoBadge(!!props.data["bounced"]);
    };

    const unsubscribedCell = (props: CustomCellRendererProps) => {
        if (verificationStatus === "queued" || verificationStatus === "processing" || verificationStatus === "skipping") return dashCell;
        return yesNoBadge(!!props.data["unsubscribed"]);
    };

    const statusCell = (props: CustomCellRendererProps) => {
        const { label, color } = getStatusDisplay(props.data["verification_status"]);
        if (props.data["verification_status"] === "skipped") {
            return (
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" color={color} label={label} />
                    <Tooltip title="This contact was added, but the email verification process was manually skipped before it could finish.">
                        <InfoOutlined sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                    </Tooltip>
                </Stack>
            );
        }
        return <Chip size="small" color={color} label={label} />;
    };

    const matrixCell = (id: string) => (props: CustomCellRendererProps) => {
        if (verificationStatus !== "completed" && verificationStatus !== "skipped") return dashCell;
        const status = (props.data["verification_status"] ?? "").toLowerCase();
        const value = columnMatrix[status]?.[id];
        if (value === "yes") return yesNoBadge(true);
        if (value === "no") return yesNoBadge(false);
        return dashCell;
    };

    const boolYesGoodCell = (field: string) => (props: CustomCellRendererProps) => {
        const val = props.data[field];
        if (val === true) return <Typography color="success.main">Yes</Typography>;
        if (val === false) return <Typography color="error">No</Typography>;
        return <Typography color="text.secondary">—</Typography>;
    };

    // -------------- Column definitions --------------
    // Verification columns appear once a verification run exists for this list (processing or done).
    const showVerificationColumns =
        verificationStatus === "processing" || verificationStatus === "skipping" || verificationStatus === "completed" || verificationStatus === "skipped";

    const colDefs: ColDef[] = [
        {
			field: "esp",
			headerName: "ESP",
			width: 50,
            cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
			cellRenderer: (params: any) => {
				const esp = (params.data?.esp ?? "Unknown").toLowerCase();

				const logo = ESP_LOGOS[esp];

				if (!logo) {
					const label =
						esp === "other" || esp === "others"
							? "Others"
							: "Unknown";

					return (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                height: "100%",
                                margin: 0,
                                padding: 0,
                            }}
                        >
                            <span className="text-xs text-muted-foreground text-center">
                                {label}
                            </span>
                        </Box>
                    );
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                            margin: 0,
                            padding: 0,
                        }}
                    >
                        <Tooltip title={logo.label} arrow>
                            <img
                                src={logo.src}
                                alt={logo.label}
                                style={{
                                    width: esp === "zoho" ? 24 : 20,
                                    height: esp === "zoho" ? 24 : 20,
                                    display: "block",
                                    objectFit: "contain",
                                }}
                            />
                        </Tooltip>
                    </Box>
                );
            },
        },
        { field: "email", headerName: "Contact Email ID", flex: 2, minWidth: 220, sortable: true },
        { field: "uid", headerName: "Contact UID", flex: 2, minWidth: 200, sortable: false },
    ];

    additionalColumnNames.slice(0, 4).forEach((col) => {
        colDefs.push({ field: col, headerName: col, minWidth: 140, sortable: true });
    });

    if (showVerificationColumns) {
        colDefs.push({
            field: "verification_status",
            headerName: "Verification Status",
            cellRenderer: statusCell,
            sortable: true,
            minWidth: 160,
            headerTooltip:
                verificationStatus === "processing"
                    ? "We are verifying your contact list. The final status will update automatically once the entire list is fully checked."
                    : undefined,
        });
        verificationColumns.forEach(({ id, label }) => {
            colDefs.push({
                field: id,
                headerName: label,
                cellRenderer: matrixCell(id),
                sortable: true,
                minWidth: 150,
            });
        });
    }

    colDefs.push(
        { field: "bounced", headerName: "Bounced", cellRenderer: bouncedCell, sortable: true, minWidth: 120 },
        { field: "unsubscribed", headerName: "Unsubscribed", cellRenderer: unsubscribedCell, sortable: true, minWidth: 140 }
    );

    if (verificationStatus === "completed" || verificationStatus === "skipped") {
        colDefs.push(
            { field: "ver_deliverable", headerName: "Deliverable", cellRenderer: boolYesGoodCell("ver_deliverable"), sortable: false, minWidth: 130 },
            { field: "ver_host_exists", headerName: "Host Exists", cellRenderer: boolYesGoodCell("ver_host_exists"), sortable: false, minWidth: 130 },
            { field: "ver_esp", headerName: "ESP", sortable: false, minWidth: 120 },
            { field: "ver_message", headerName: "Message", flex: 2, minWidth: 220, sortable: false }
        );
    }

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleSortChange = (field: string, direction: "asc" | "desc" | null) => {
        setSortField(direction ? field : "");
        setSortDirection(direction ?? "asc");
        setPage(1);
    };

    if (pageDataQuery.isLoading) {
        return <PageLoading />;
    }

    if (pageDataQuery.error) {
        return (
            <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />
        );
    }

    return (
        <Stack direction="column" spacing={4} sx={{ pb: 4, height: "100%" }}>
            <Stack direction="row" spacing={2} alignItems="center">
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                </IconButton>
                <Typography fontWeight="bold" variant="h5">
                    Contact List: {listName}
                </Typography>
            </Stack>
            <Divider />

            {/* Summary cards — shown once verification has completed or was skipped */}
            {(verificationStatus === "completed" || verificationStatus === "skipped") && summaryCards && (
                <ContactListCards summaryCards={summaryCards} />
            )}

            {/* Verification Panel */}
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                    Email Verification
                </Typography>
                
                <Stack spacing={1.5}>
                    {/* Status row */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            gap: 2
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                    Status:
                                </Typography>
                                {verificationStatusChip()}
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                    Submitted:
                                </Typography>
                                {verificationTaskUid ? (
                                    <Chip size="small" color="success" label="Yes" />
                                ) : (
                                    <Chip size="small" color="default" label="No" />
                                )}
                            </Stack>
                        </Stack>

                        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                            {actionButton()}
                        </Box>
                    </Box>

                    {/* Queued State */}
                    {verificationStatus === "queued" && (
                        <Typography variant="caption" color="text.secondary">
                            Verification is queued and will start shortly.
                        </Typography>
                    )}

                    {/* Progress bar */}
                    {(verificationStatus === "processing" || verificationStatus === "skipping") && (
                        <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <LinearProgress
                                    variant="determinate"
                                    value={progressPercent}
                                    sx={{
                                        flex: 1,
                                        height: 8,
                                        borderRadius: 4,
                                        "& .MuiLinearProgress-bar": { borderRadius: 4 },
                                    }}
                                />
                                <Typography variant="caption" sx={{ minWidth: 35 }}>
                                    {progressPercent}%
                                </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                {processedCount} / {totalContactsCount} contacts processed
                            </Typography>
                        </Stack>
                    )}

                    {/* Results link */}
                    {verificationTaskUid && (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<OpenInNew fontSize="small" />}
                                href={`https://mailer.draftss.com/get-results?task_type=verify&task_uid=${verificationTaskUid}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textTransform: "none", px: 0 }}
                            >
                                View Results
                            </Button>
                            <Tooltip title="Copy Task UID">
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<ContentCopy fontSize="small" />}
                                    onClick={handleCopyUid}
                                    sx={{ textTransform: "none" }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            maxWidth: { xs: 140, sm: 200 },
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {verificationTaskUid}
                                    </Typography>
                                </Button>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
            </Paper>

            {/* Fixed/min height so the grid keeps a usable size even when the summary cards and
                verification panel above consume most of the viewport. Without this, "height: 100%"
                only receives the flex leftover and collapses the grid to a single clipped row. */}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 1 }}>
                <Tabs value={tabValue} onChange={handleTabChange} sx={{
                    '& .MuiTabs-flexContainer': {
                        flexWrap: 'wrap',
                    },
                }}>
                    <Tab label="Data" />
                    <Tab label="Campaigns" />
                </Tabs>
            </Box> 
             <Box sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: '400px',
                    bgcolor: 'background.paper', 
                    p: 1, 
                    borderRadius: 2, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    overflow: 'hidden'
                }}>
                {tabValue === 0 && (
                    <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                        <PaginatedServerSideDataTable
                            key={verificationStatus}
                            columns={colDefs}
                            columnLabels={columnLabels}
                            paginationData={pageData}
                            loading={
                                pageDataQuery.isLoading ||
                                pageDataQuery.isFetching
                            }
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSearchChange={handleSearchChange}
                            onSortChange={handleSortChange}
                            searchPlaceholder="Search email id..."
                        />
                    </Box>
                )}
                {tabValue === 1 && (
                    <AdminUserContactListCampaigns userId={userId!} contactListUID={contactListUID!} />
                )}
            </Box>

            {/* Stop confirmation — Stop is destructive (cancels the verifier job and discards
                already-processed results), so confirm before firing the mutation. */}
            <Dialog open={stopConfirmOpen} onClose={() => setStopConfirmOpen(false)} maxWidth="xs">
                <DialogTitle>Stop verification?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Stopping cancels verification immediately. Contacts already processed will
                        not have their results saved, and the list will be marked as failed. You can
                        re-run verification later. To keep already-verified results instead, use Skip.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setStopConfirmOpen(false)}
                        color="inherit"
                        sx={{ textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={stopVerificationMutation.isPending}
                        onClick={() => {
                            stopVerificationMutation.mutate();
                            setStopConfirmOpen(false);
                        }}
                        sx={{ textTransform: "none" }}
                    >
                        Stop verification
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

interface ContactListCardsProps {
    summaryCards: SummaryCards;
}

function ContactListCards({ summaryCards }: ContactListCardsProps) {
    const cards: { label: string; value: number; tooltip: string; breakdownKey?: EspBreakdownSummaryKey }[] = [
        { label: "Total Contacts", value: summaryCards.contacts_uploaded, tooltip: "Total number of contacts in this list before any filtering or verification." },
        { label: "Invalid Emails", value: summaryCards.invalid_emails, tooltip: "Contacts with invalid, non-existent, or incorrectly formatted email addresses.", breakdownKey: "invalid_emails" },
        { label: "Unverifiable", value: summaryCards.unverifiable_emails, tooltip: "The mail server uses a strict delay to stop spam. We could not verify these email addresses because the server asked us to wait or the connection timed out.", breakdownKey: "unverifiable_emails" },
        { label: "Disposable Email", value: summaryCards.disposable_emails, tooltip: "Contacts using temporary or disposable email addresses that are often created for short term use.", breakdownKey: "disposable_emails" },
        { label: "Dangerous Email", value: summaryCards.dangerous_emails, tooltip: "Contacts with email addresses identified as high risk, abusive, or likely to cause delivery issues.", breakdownKey: "dangerous_emails" },
        { label: "Inbox Full", value: summaryCards.full_inbox_emails, tooltip: "Contacts whose inboxes cannot currently receive new emails because storage limits have been reached.", breakdownKey: "full_inbox_emails" },
        { label: "Catch All", value: summaryCards.catch_all_emails, tooltip: "Contacts whose domains accept all incoming emails, making deliverability uncertain.", breakdownKey: "catch_all_emails" },
        { label: "Unverified Webmail", value: summaryCards.webmail_emails, tooltip: "Contacts hosted on public email providers such as Yahoo, iCloud, etc.", breakdownKey: "webmail_emails" },
        { label: "Skipped", value: summaryCards.skipped_emails, tooltip: "Verification was manually skipped for these contacts while the process was running. You can still choose to include them in your campaigns, but their deliverability is not guaranteed." },
        { label: "Unsubscribed", value: summaryCards.unsubscribed, tooltip: "Contacts who have opted out and no longer wish to receive emails." },
        { label: "Total Deliverable", value: summaryCards.contacts_after_verification, tooltip: "Total number of contacts with verified email addresses that are safe to send emails to.", breakdownKey: "contacts_after_verification" },
    ];
    const espLabels: { key: EspBreakdownKey; label: string }[] = [
        { key: "google", label: "Google" },
        { key: "microsoft", label: "Microsoft" },
        { key: "yahoo", label: "Yahoo" },
        { key: "zoho", label: "Zoho" },
        { key: "others", label: "Others" },
        { key: "unknown", label: "Unknown" },
    ];
    return (
        <Box
            sx={{
                display: "grid",
                gap: { xs: 1.5, sm: 2 },
                gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(5, 1fr)",
                },
            }}
        >
            {cards.map(({ label, value, tooltip, breakdownKey }) => {
                const breakdown = breakdownKey ? summaryCards.esp_breakdowns?.[breakdownKey] : undefined;
                return (
                    <Card key={label} variant="outlined">
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight="medium"
                                    sx={{
                                        lineHeight: 1.2,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {label}
                                </Typography>
                                <Tooltip title={tooltip}>
                                    <InfoOutlined sx={{ fontSize: 13, color: "text.disabled", cursor: "help", flexShrink: 0 }} />
                                </Tooltip>
                            </Stack>
                            {breakdown ? (
                                <Tooltip
                                    arrow
                                    title={
                                        <Box sx={{ minWidth: 160 }}>
                                            <Typography component="div" variant="caption" fontWeight="bold" color="inherit" sx={{ mb: 1 }}>
                                                ESP breakdown
                                            </Typography>
                                            {espLabels.map(({ key, label: espLabel }) => (
                                                <Stack key={key} direction="row" justifyContent="space-between" spacing={3}>
                                                    <Typography variant="caption" color="inherit">{espLabel}</Typography>
                                                    <Typography variant="caption" color="inherit" fontWeight="bold">{breakdown[key]}</Typography>
                                                </Stack>
                                            ))}
                                        </Box>
                                    }
                                >
                                    <Box
                                        component="button"
                                        type="button"
                                        aria-label={`${label} ESP breakdown`}
                                        sx={{
                                            p: 0,
                                            border: 0,
                                            bgcolor: "transparent",
                                            color: "text.primary",
                                            cursor: "help",
                                            fontFamily: "inherit",
                                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                            fontWeight: "bold",
                                            lineHeight: 1.6,
                                            "&:focus-visible": {
                                                borderRadius: 0.5,
                                                outline: "2px solid",
                                                outlineColor: "primary.main",
                                                outlineOffset: 2,
                                            },
                                        }}
                                    >
                                        {value}
                                    </Box>
                                </Tooltip>
                            ) : (
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                                    {value}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );
}


interface CampaignPaginationData {
    user_email: string
    data: Campaign[]
    total_count: number
    total_pages: number
    current_page: number
    page_size: number
}

interface Campaign {
    uid: string
    workspace: string
    created_on_ts: number
    name: string
    custom_emails_per_day: number
    status: string
    scheduled_start_datetime_ts: number | null
    reply_to_address: string | null
    sending_domains: Array<string>
    bounces: number
    total_replies: number
    total_sent: number
    campaign_type: string
}

export function AdminUserContactListCampaigns(props: {userId: string, contactListUID: string}) {
    const theme = useTheme();

    const [pageData, setPageData] = useState<CampaignPaginationData>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    
    const adminUserContactListCampaignsQuery = useQuery({
		queryKey: [
			"adminUserCampaignsQuery",
			props.userId,
            props.contactListUID,
			page,
			pageSize,
			search
		],
		queryFn: () =>
			authenticateAndFetchData(
				`/admin/user-contact-list-campaigns/?user_id=${props.userId}&page=${page}&page_size=${pageSize}&search=${search}&contact_list_uid=${props.contactListUID}`
			),
		gcTime: 0,
		refetchOnWindowFocus: false,
		retry: retryFn,
	});

    useEffect(() => {
		if (adminUserContactListCampaignsQuery.data?.data) {
			const response = adminUserContactListCampaignsQuery.data.data;

			setPageData({
				user_email: response.user_email,
				data: response.data || [],
				total_count: response.total_count || 0,
				total_pages: response.total_pages || 0,
				current_page: response.current_page || 1,
				page_size: response.page_size || 50,
			});
		}
	}, [adminUserContactListCampaignsQuery.data]);

    const CampaignDetailsLink = (cellProps: CustomCellRendererProps) => {
        return (
            <Link
                to={urls["adminUserCampaignDetails"].replace(":userId", props.userId || "").replace(":campaignUID", cellProps.data["uid"])}
                style={{color: theme.palette.text.primary}}>
                {cellProps.value}
            </Link>
        )
    }

    const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

    function timestampFormatter(params: ValueFormatterParams) {
        if (params.value !== null) {
            return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
        } else {
            return "---"
        }
    }

    function sendingDomainsFormatter(params: ValueFormatterParams) {
        return params.value.join(", ");
    }

    function campaignStatusChipColor(status: string) {
        switch (status) {
            case "creating":
                return "warning";

            case "created":
                return "default";

            case "scheduled":
                return "primary";

            case "running":
                return "primary";

            case "complete":
                return "success";

            case "cancelled":
                return "error";

            default:
                return "default";
        }
    }

    const StatusBadge = (params: CustomCellRendererProps) => {
        return (
            <Chip size={"small"} color={campaignStatusChipColor(params.value)} label={params.value}/>
        )
    }
    
    const columnCustomizerLabel = {
        name: "Campaign Name",
        uid: "Campaign UID",
        created_on_ts: "Created On",
        workspace_name: "Workspace",
        workspace_id: "Workspace Name",
        scheduled_start_datetime_ts: "Scheduled Start",
        total_sent: "Total Emails Sent",
        total_replies: "Total Replies",
        reply_to_address: "Reply To",
        sending_domains: "Subdomains",
        bounces: "Bounced",
        status: "Status",				
    };

    // Columns for campaigns table.
    const columnDefs: ColDef[] = [
        {field: "name", headerName: "Campaign Name", cellRenderer: CampaignDetailsLink, flex: 2},
        {field: "uid", headerName: "Campaign UID"},
        {
            field: "created_on_ts",
            headerName: "Created On",
            valueFormatter: timestampFormatter,
            comparator: (valueA, valueB, nodeA, nodeB) => {
                return nodeA.data["created_on_ts"] > nodeB.data["created_on_ts"] ? 1 : -1;
            },
            flex: 2,
        },
        {field: "workspace_name", headerName: "Workspace Name"},
        { field: "workspace_id", headerName: "Workspace ID" },
        // {field: "custom_emails_per_day", headerName: "Custom Email Limit"},
        {
            field: "scheduled_start_datetime_ts",
            headerName: "Scheduled Start",
            valueFormatter: timestampFormatter,
            comparator: (valueA, valueB, nodeA, nodeB) => {
                let value1 = nodeA.data["scheduled_start_datetime_ts"] !== null ? nodeA.data["scheduled_start_datetime_ts"] : 0;
                let value2 = nodeB.data["scheduled_start_datetime_ts"] !== null ? nodeB.data["scheduled_start_datetime_ts"] : 0;

                if (value1 === value2) return 0;
                return value1 > value2 ? 1 : -1;
            },
        },
        {field: "total_sent", headerName: "Total Emails Sent"},
        {field: "total_replies", headerName: "Total Replies"},
        {field: "reply_to_address", headerName: "Reply To"},
        {field: "sending_domains", headerName: "Subdomains", valueFormatter: sendingDomainsFormatter},
        {field: "bounces", headerName: "Bounced"},    
        {
            field: "campaign_type",
            headerName: "Campaign Type",
            valueFormatter: ({ value }) =>
                value === null || value === undefined || value === "" ? "---" : value,
        },
        {field: "status", headerName: "Status", cellRenderer: StatusBadge},
    ]

    if (!pageData && adminUserContactListCampaignsQuery.isLoading) {
        return (
            <PageLoading/>
        )

    } else if (adminUserContactListCampaignsQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={adminUserContactListCampaignsQuery.error as unknown as ApiRequestFailed}/>

    } else if (pageData) {
        return (
            <Box sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
                <PaginatedServerSideDataTable
                    columns={columnDefs}
                    paginationData={pageData}
                    loading={adminUserContactListCampaignsQuery.isFetching}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSearchChange={handleSearchChange}
                    searchPlaceholder="Search campaign name, uid, status..."
                    columnLabels={columnCustomizerLabel}
                />
            </Box>
        )
    } else {
		// Ideally it should not reach here.
		return <></>
	}
}